import React, { useState, useEffect } from "react";
import { FaTimes, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { BASE_URL, UPLOAD_URL } from "../../../utils/constants";

const ProfileGallery = ({ profile }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Combine main image + gallery array safely
  const allImages = [...(profile.gallery || [])].filter(Boolean);

  const openModal = (index) => {
    setCurrentIndex(index);
    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") closeModal();
    if (e.key === "ArrowRight") nextImage(e);
    if (e.key === "ArrowLeft") prevImage(e);
  };

  useEffect(() => {
    if (isModalOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [isModalOpen]);

  if (!allImages.length) return null;

  return (
    <>
      <div className="bg-white rounded-lg shadow-2xl p-4 mt-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-800">Photo Gallery</h3>
          <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
            {allImages.length} {allImages.length === 1 ? "Photo" : "Photos"}
          </span>
        </div>

        <div className="flex overflow-x-auto gap-3 pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          {allImages.map((img, index) => (
            <div
              key={index}
              className="relative flex-shrink-0 group cursor-pointer"
              onClick={() => openModal(index)}
            >
              <img
                src={`${UPLOAD_URL}/users/gallery/${img}`}
                alt={`Gallery ${index + 1}`}
                className="w-24 h-24 sm:w-28 sm:h-28 object-cover rounded-lg shadow-md hover:shadow-xl transition-all duration-200 border-2 border-transparent hover:border-blue-500"
              />
              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 rounded-lg transition-all duration-200 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                  />
                </svg>
              </div>
              {/* Image number badge */}
              <div className="absolute bottom-1 right-1 bg-black bg-opacity-70 text-white text-xs px-2 py-0.5 rounded">
                {index + 1}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ---------- Modal Viewer ---------- */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-[9999] bg-black bg-opacity-95 flex justify-center items-center p-4"
          onClick={closeModal}
        >
          <div
            className="relative w-full h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              className="fixed top-4 right-4 z-[10000] bg-red-600 text-white rounded-full p-3 hover:bg-red-700 transition-all shadow-lg hover:scale-110"
              onClick={closeModal}
              aria-label="Close modal"
            >
              <FaTimes size={20} />
            </button>

            {/* Image Counter */}
            <div className="fixed top-4 left-4 z-[10000] bg-black bg-opacity-70 text-white px-4 py-2 rounded-full text-sm font-semibold">
              {currentIndex + 1} / {allImages.length}
            </div>

            {/* Main Image */}
            <div className="relative max-w-7xl max-h-full flex items-center justify-center">
              <img
                src={`${UPLOAD_URL}/users/gallery/${allImages[currentIndex]}`}
                alt={`Gallery ${currentIndex + 1}`}
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              />
            </div>

            {/* Navigation Arrows */}
            {allImages.length > 1 && (
              <>
                {/* Previous */}
                <button
                  className="fixed left-4 top-1/2 transform -translate-y-1/2 z-[10000] bg-white text-gray-800 p-4 rounded-full shadow-xl hover:bg-gray-100 transition-all hover:scale-110"
                  onClick={prevImage}
                  aria-label="Previous image"
                >
                  <FaChevronLeft size={24} />
                </button>

                {/* Next */}
                <button
                  className="fixed right-4 top-1/2 transform -translate-y-1/2 z-[10000] bg-white text-gray-800 p-4 rounded-full shadow-xl hover:bg-gray-100 transition-all hover:scale-110"
                  onClick={nextImage}
                  aria-label="Next image"
                >
                  <FaChevronRight size={24} />
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ProfileGallery;
